<template>
  <div>
    <ValidationProvider v-slot="{ errors }">
      <b-field
        :type="{ 'is-danger': errors.length > 0 }"
        :message="errors[0]"
      >
        <template #label>
          TX File
          <b-tag :type="txFileStatusType" size="is-small" class="has-margin-left-10">
            {{ txFileStatusLabel }}
          </b-tag>
        </template>
        <input v-show="false" v-model="file" />
      </b-field>

      <!-- Existing Assets List -->
      <div v-if="items.length === 0" class="has-text-grey">
        No TX file linked yet.
      </div>
      <div v-if="items.length > 0">
        <ul>
          <li v-for="item in items" :key="item.id" class="has-margin-bottom-5">
            <span class="icon has-text-success">
              <i class="mdi mdi-file-check"></i>
            </span>
            <a :href="item.path" target="_blank">{{ item.name }}</a>
            <a
              href=""
              class="has-margin-left-10 has-text-danger"
              @click.prevent="remove(item)"
            >
              <span class="icon"><i class="mdi mdi-delete"></i></span>
            </a>
          </li>
        </ul>
      </div>

      <!-- Action Buttons -->
      <div v-if="!showUpload && !showStorageBrowser" class="buttons">
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="upload"
          @click="showUpload = true"
        >
          Upload from Local
        </b-button>
        <b-button
          type="is-info"
          size="is-small"
          icon-left="cloud-search"
          @click="openStorageBrowser"
        >
          Select from Storage
        </b-button>
      </div>

      <b-button
        v-if="showUpload || showStorageBrowser"
        type="is-light"
        size="is-small"
        @click="cancelSelection"
      >
        Cancel
      </b-button>
    </ValidationProvider>

    <!-- Storage Browser Section -->
    <div v-if="showStorageBrowser" class="has-margin-top-15">
      <b-message type="is-info" has-icon>
        <p><strong>Select TX file from configured storage</strong></p>
        <p class="is-size-7">
          Browse files that already exist in your storage (e.g., from encoding/stitching jobs).
        </p>
      </b-message>

      <!-- Storage Config Selector -->
      <b-field label="Storage Location">
        <b-select
          v-model="selectedStorageConfigId"
          placeholder="Select a storage"
          expanded
          :loading="loadingStorageConfigs"
          @input="onStorageSelected"
        >
          <option
            v-for="config in storageConfigs"
            :key="config.id"
            :value="config.id"
          >
            {{ config.name }} ({{ config.bucket_name }})
          </option>
        </b-select>
      </b-field>

      <!-- File Browser -->
      <div v-if="selectedStorageConfigId" class="storage-browser">
        <!-- Breadcrumb Navigation -->
        <div class="level mb-3">
          <div class="level-left">
            <div class="level-item">
              <nav class="breadcrumb is-small mb-0" aria-label="breadcrumbs">
                <ul>
                  <li :class="{ 'is-active': currentPath === '' }">
                    <a @click="browseTo('')">
                      <span class="icon is-small"><i class="mdi mdi-folder-home"></i></span>
                      <span>Root</span>
                    </a>
                  </li>
                  <li v-for="(part, index) in pathParts" :key="index" :class="{ 'is-active': index === pathParts.length - 1 }">
                    <a @click="browseTo(pathParts.slice(0, index + 1).join('/'))">{{ part }}</a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item" v-if="currentPath">
              <b-button
                type="is-light"
                icon-left="arrow-up"
                size="is-small"
                @click="goUpOneLevel"
              >
                Up
              </b-button>
            </div>
          </div>
        </div>

        <!-- Manual Path Entry -->
        <b-field grouped>
          <b-input
            v-model="storageBrowsePath"
            placeholder="Enter folder path (e.g., output/tx-mezzanine)"
            icon="folder"
            size="is-small"
            expanded
            @keyup.enter="browseToManualPath"
          />
          <p class="control">
            <b-button
              type="is-primary"
              size="is-small"
              icon-left="folder-search"
              :loading="browsingStorage"
              @click="browseToManualPath"
            >
              Go to Path
            </b-button>
          </p>
        </b-field>

        <!-- Folders Section -->
        <div v-if="browseFolders.length > 0" class="folders-section mb-4">
          <h6 class="is-size-7 has-text-weight-semibold has-text-grey mb-2">
            <span class="icon is-small"><i class="mdi mdi-folder-multiple"></i></span>
            Folders ({{ browseFolders.length }}) - Click to navigate
          </h6>
          <div class="folder-grid">
            <div
              v-for="folder in browseFolders"
              :key="folder.path"
              class="folder-item"
              @click="browseTo(folder.path)"
            >
              <span class="icon has-text-warning"><i class="mdi mdi-folder mdi-24px"></i></span>
              <span class="folder-name">{{ folder.name }}</span>
            </div>
          </div>
        </div>

        <!-- No folders message -->
        <div v-else-if="!browsingStorage && browseResults.length > 0" class="notification is-info is-light is-small py-2 mb-3">
          <span class="icon is-small"><i class="mdi mdi-information"></i></span>
          <span class="is-size-7">No subfolders in this location. Showing files only.</span>
        </div>

        <!-- Files Section -->
        <div v-if="browseFiles.length > 0" class="files-section">
          <h6 class="is-size-7 has-text-weight-semibold has-text-grey mb-2">
            <span class="icon is-small"><i class="mdi mdi-file-multiple"></i></span>
            Files ({{ browseFiles.length }})
          </h6>
          <div class="file-list-container">
            <div
              v-for="file in browseFiles"
              :key="file.path"
              class="file-item"
              :class="{ 'is-selected': selectedStorageFile === file.path }"
              @click="selectStorageFile(file)"
            >
              <span class="icon">
                <i class="mdi mdi-file-video"></i>
              </span>
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size has-text-grey is-size-7">
                {{ formatFileSize(file.size) }}
              </span>
              <span class="file-actions">
                <b-button
                  v-if="isVideoFile(file.name)"
                  type="is-light"
                  size="is-small"
                  icon-left="play"
                  title="Preview video"
                  @click.stop="previewFile(file)"
                >
                  Preview
                </b-button>
              </span>
            </div>
          </div>
        </div>

        <!-- Load More Button -->
        <div v-if="isTruncated && !browsingStorage" class="has-text-centered py-3">
          <div class="notification is-info is-light py-2 mb-3">
            <span class="icon is-small"><i class="mdi mdi-information"></i></span>
            <span class="is-size-7">
              Showing {{ totalFoldersLoaded }} folders and {{ totalFilesLoaded }} files. More items available.
            </span>
          </div>
          <b-button
            type="is-primary"
            icon-left="plus"
            :loading="loadingMore"
            @click="loadMoreResults"
          >
            Load More Items
          </b-button>
        </div>

        <!-- Items loaded count (when all loaded) -->
        <div v-else-if="!browsingStorage && browseResults.length > 0 && !isTruncated" class="has-text-centered py-2">
          <span class="is-size-7 has-text-grey">
            Showing all {{ totalFoldersLoaded }} folders and {{ totalFilesLoaded }} files
          </span>
        </div>

        <!-- Loading state -->
        <div v-if="browsingStorage" class="has-text-centered py-4">
          <b-loading :is-full-page="false" :active="true" />
        </div>

        <!-- Empty state -->
        <div v-if="!browsingStorage && !loadingMore && browseResults.length === 0 && hasSearched" class="has-text-centered py-4">
          <span class="icon is-large has-text-grey-light">
            <i class="mdi mdi-folder-open-outline mdi-48px"></i>
          </span>
          <p class="has-text-grey">This folder is empty</p>
        </div>

        <!-- Link Button -->
        <div v-if="selectedStorageFile" class="has-margin-top-15">
          <b-message type="is-success" has-icon>
            <p><strong>Selected:</strong> {{ getFilenameFromKey(selectedStorageFile) }}</p>
            <p class="is-size-7">{{ selectedStorageFile }}</p>
          </b-message>
          <b-button
            type="is-success"
            icon-left="link"
            :loading="linkingFile"
            @click="linkStorageFile"
          >
            Link This File as TX
          </b-button>
        </div>
      </div>
    </div>

    <!-- Video Preview Modal with Enhanced MediaPlayer -->
    <b-modal
      :active.sync="showVideoPreview"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'x', 'outside']"
      width="1024px"
      @close="closeVideoPreview"
    >
      <template #default>
        <div class="modal-card" style="width: 1024px;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <span class="icon"><i class="mdi mdi-play-circle"></i></span>
              {{ previewFilename }}
            </p>
            <button type="button" class="delete" @click="closeVideoPreview" />
          </header>
          <section class="modal-card-body video-preview-body">
            <div v-if="loadingPreview" class="has-text-centered py-6">
              <b-loading :is-full-page="false" :active="true" />
              <p class="mt-4">Loading video preview...</p>
            </div>
            <div v-else-if="previewError" class="has-text-centered py-6">
              <span class="icon is-large has-text-danger">
                <i class="mdi mdi-alert-circle mdi-48px"></i>
              </span>
              <p class="has-text-danger">{{ previewError }}</p>
            </div>
            <div v-else-if="previewUrl">
              <MediaPlayer
                :url="previewUrl"
                :source-filename="previewFilename"
                :frame-rate="25"
                :show-info="false"
                @playback-error="handleMediaPlayerError"
              />
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="closeVideoPreview">Close</b-button>
            <b-button
              v-if="previewFileData"
              type="is-success"
              icon-left="link"
              @click="selectFileFromPreview"
            >
              Link This File
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>

    <!-- Upload Section -->
    <div v-if="showUpload" class="has-margin-top-15">
      <!-- TX Filename Preview -->
      <b-message v-if="txFilename" type="is-info" has-icon>
        <p><strong>Expected filename:</strong></p>
        <code>{{ expectedTxFilename }}</code>
        <p class="has-margin-top-10 is-size-7">
          Please ensure your file is named exactly as shown above before uploading.
          The system will validate that the filename matches.
        </p>
      </b-message>

      <b-message v-else type="is-warning" has-icon>
        <p>
          <strong>TX Filename not set</strong>
        </p>
        <p>
          Please ensure all required metadata is set on the episode (Supplier,
          Resolution, Language, Age Restriction) before uploading.
        </p>
      </b-message>

      <!-- File Upload Dropzone -->
      <b-upload
        v-if="txFilename"
        v-model="fileToUpload"
        drag-drop
        :loading="uploading"
        accept=".mxf,.mp4,.mov,.ts,.m4v"
        @input="startUpload"
      >
        <section class="section">
          <div class="content has-text-centered">
            <p>
              <b-icon icon="upload" size="is-large"> </b-icon>
            </p>
            <p>Drop your TX file here or click to upload</p>
            <p class="is-size-7 has-text-grey">
              Accepted formats: MXF, MP4, MOV, TS, M4V
            </p>
          </div>
        </section>
      </b-upload>

      <!-- Upload Progress -->
      <div v-if="uploading" class="has-margin-top-15">
        <p class="has-margin-bottom-5">
          <strong>Uploading:</strong> {{ uploadStatus }}
        </p>
        <b-progress
          type="is-info"
          :value="uploadProgress"
          show-value
          format="percent"
        ></b-progress>
      </div>

      <!-- QC Status after upload -->
      <b-message
        v-if="uploadComplete && qcJobId"
        type="is-success"
        has-icon
        class="has-margin-top-15"
      >
        <p><strong>Upload Complete!</strong></p>
        <p>QC job #{{ qcJobId }} has been automatically triggered.</p>
        <p class="is-size-7">
          The system will verify segment durations, audio levels, and check for
          black frames.
        </p>
      </b-message>
    </div>
  </div>
</template>

<script>
import MediaAssetsApi from '@/api/mediaAssets'
import StorageConfigApi from '@/api/storageConfig'
import storageBrowseApi from '@/api/storageBrowse'
import axios from '@/axios'
import plainAxios from 'axios'
import MediaPlayer from '@/components/MediaPlayer.vue'

export default {
  name: 'UploadMediaAssets',
  components: {
    MediaPlayer
  },
  inheritAttrs: false,
  props: {
    mediaId: {
      type: Number,
      default: null
    },
    episodeId: {
      type: Number,
      default: null
    },
    txFilename: {
      type: String,
      default: null
    },
    // TX File Status props
    storagePath: {
      type: String,
      default: null
    },
    stitchedFilePath: {
      type: String,
      default: null
    },
    stitchStatus: {
      type: String,
      default: null
    },
    stitchQcStatus: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showUpload: false,
      uploading: false,
      uploadProgress: 0,
      uploadStatus: 'Preparing...',
      uploadComplete: false,
      qcJobId: null,
      fileToUpload: null,
      file: null,
      items: [],
      // Storage browser state
      showStorageBrowser: false,
      loadingStorageConfigs: false,
      storageConfigs: [],
      selectedStorageConfigId: null,
      storageBrowsePath: '',
      browsingStorage: false,
      browseResults: [],
      selectedStorageFile: null,
      selectedStorageFileInfo: null,
      linkingFile: false,
      hasSearched: false,
      currentPath: '',
      // Pagination state for folder browser
      nextContinuationToken: null,
      isTruncated: false,
      loadingMore: false,
      totalFoldersLoaded: 0,
      totalFilesLoaded: 0,
      // Expected extension from encoding preset
      expectedExtension: 'mp4',
      // Video preview state
      showVideoPreview: false,
      previewUrl: null,
      previewFilename: '',
      previewFileData: null,
      loadingPreview: false,
      previewError: null,
      usingProxyStream: false // True when using backend proxy (CORS fallback)
    }
  },
  computed: {
    // Breadcrumb path parts
    pathParts () {
      if (!this.currentPath) return []
      return this.currentPath.split('/').filter(p => p)
    },
    // Filter folders from browse results
    browseFolders () {
      return this.browseResults.filter(item => item.type === 'folder')
    },
    // Filter files from browse results
    browseFiles () {
      return this.browseResults.filter(item => item.type === 'file')
    },
    // Full expected filename with extension
    expectedTxFilename() {
      if (!this.txFilename) return null
      return `${this.txFilename}.${this.expectedExtension}`
    },
    // Check if TX file exists
    hasTxFile() {
      return !!(this.storagePath || this.stitchedFilePath)
    },
    // Get status type for Buefy tag
    txFileStatusType() {
      // 1. NO TX FILE - check if pipeline is in progress
      if (!this.hasTxFile) {
        if (this.stitchStatus === 'processing' || this.stitchStatus === 'queued' || this.stitchStatus === 'pending') {
          return 'is-warning' // Processing
        }
        if (this.stitchStatus === 'failed') {
          return 'is-danger' // Stitch Failed
        }
        return 'is-light' // No TX File
      }

      // 2. TX FILE EXISTS - check QC status
      if (this.stitchQcStatus === 'passed') return 'is-success' // TX Ready
      if (this.stitchQcStatus === 'passed_with_warnings') return 'is-success' // TX Ready*
      if (this.stitchQcStatus === 'overridden') return 'is-info' // QC Overridden
      if (this.stitchQcStatus === 'failed') return 'is-danger' // QC Failed
      if (this.stitchQcStatus === 'pending' || this.stitchQcStatus === 'processing') return 'is-warning' // QC Pending

      // TX file exists but no QC run (direct upload/assignment)
      return 'is-info' // Assigned (No QC)
    },
    // Get status label
    txFileStatusLabel() {
      // 1. NO TX FILE - check if pipeline is in progress
      if (!this.hasTxFile) {
        if (this.stitchStatus === 'processing') return 'Processing...'
        if (this.stitchStatus === 'queued') return 'Queued'
        if (this.stitchStatus === 'pending') return 'Pending Stitch'
        if (this.stitchStatus === 'failed') return 'Stitch Failed'
        return 'No TX File'
      }

      // 2. TX FILE EXISTS - check QC status
      if (this.stitchQcStatus === 'passed') return 'TX Ready'
      if (this.stitchQcStatus === 'passed_with_warnings') return 'TX Ready*'
      if (this.stitchQcStatus === 'overridden') return 'QC Overridden'
      if (this.stitchQcStatus === 'failed') return 'QC Failed'
      if (this.stitchQcStatus === 'processing') return 'QC Running...'
      if (this.stitchQcStatus === 'pending') return 'QC Pending'

      // TX file exists but no QC run (direct upload/assignment)
      return 'TX Assigned'
    }
  },
  async created() {
    await this.loadData()
    await this.loadExpectedExtension()
  },
  methods: {
    async loadData() {
      this.items = await MediaAssetsApi.getAll(this.mediaId, this.episodeId)
    },

    async loadExpectedExtension() {
      // Load expected extension from encoding preset via validation endpoint
      if (!this.txFilename) return

      try {
        const body = { filename: 'test.mp4' }
        if (this.episodeId) {
          body.episodeId = this.episodeId
        } else {
          body.mediaId = this.mediaId
        }
        const response = await axios.post('/api/media-assets/validate-filename', body)
        this.expectedExtension = response.data.expectedExtension || 'mp4'
      } catch (error) {
        console.error('Failed to load expected extension:', error)
        // Default to mp4 if we can't get the expected extension
        this.expectedExtension = 'mp4'
      }
    },

    cancelSelection() {
      this.showUpload = false
      this.showStorageBrowser = false
      this.selectedStorageFile = null
      this.browseResults = []
      this.hasSearched = false
      this.currentPath = ''
      this.storageBrowsePath = ''
      this.nextContinuationToken = null
      this.isTruncated = false
      this.totalFoldersLoaded = 0
      this.totalFilesLoaded = 0
    },

    async openStorageBrowser() {
      this.showStorageBrowser = true
      this.loadingStorageConfigs = true
      try {
        // Use the storage-browse endpoint which is available to all logged-in users
        const response = await axios.get('/api/storage-browse/configs')
        this.storageConfigs = response.data
      } catch (error) {
        console.error('Failed to load storage configs:', error)
        this.$buefy.toast.open({
          message: 'Failed to load storage configurations',
          type: 'is-danger'
        })
      } finally {
        this.loadingStorageConfigs = false
      }
    },

    onStorageSelected() {
      // Reset file selection when storage changes
      this.browseResults = []
      this.selectedStorageFile = null
      this.hasSearched = false
      this.currentPath = ''
      this.storageBrowsePath = ''
      this.nextContinuationToken = null
      this.isTruncated = false
      this.totalFoldersLoaded = 0
      this.totalFilesLoaded = 0
      // Auto-browse root when storage is selected
      this.browseStorage()
    },

    async browseTo(path) {
      this.currentPath = path.replace(/\/$/, '') // Remove trailing slash
      this.storageBrowsePath = this.currentPath // Keep manual input in sync
      await this.browseStorage()
    },

    goUpOneLevel() {
      if (!this.currentPath) return
      const parts = this.currentPath.split('/').filter(p => p)
      parts.pop() // Remove last part
      this.browseTo(parts.join('/'))
    },

    browseToManualPath() {
      if (!this.storageBrowsePath) {
        this.browseTo('')
        return
      }
      // Clean the path - remove leading/trailing slashes
      const cleanPath = this.storageBrowsePath.trim().replace(/^\/+|\/+$/g, '')
      this.browseTo(cleanPath)
    },

    async browseStorage(loadMore = false) {
      if (!this.selectedStorageConfigId) return

      if (loadMore) {
        this.loadingMore = true
      } else {
        this.browsingStorage = true
        this.hasSearched = true
        this.selectedStorageFile = null
        // Reset pagination state when starting fresh
        this.nextContinuationToken = null
        this.isTruncated = false
        this.totalFoldersLoaded = 0
        this.totalFilesLoaded = 0
      }

      try {
        const result = await storageBrowseApi.browse(
          this.selectedStorageConfigId,
          this.currentPath,
          '', // prefix
          loadMore ? this.nextContinuationToken : null
        )

        if (loadMore) {
          // Append to existing results
          this.browseResults = [
            ...this.browseResults,
            ...result.folders,
            ...result.files
          ]
          this.totalFoldersLoaded += result.folders.length
          this.totalFilesLoaded += result.files.length
        } else {
          // Replace results
          this.browseResults = [...result.folders, ...result.files]
          this.totalFoldersLoaded = result.folders.length
          this.totalFilesLoaded = result.files.length
        }

        // Update pagination state
        this.isTruncated = result.isTruncated
        this.nextContinuationToken = result.nextContinuationToken
      } catch (error) {
        console.error('Failed to browse storage:', error)
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Failed to browse storage',
          type: 'is-danger'
        })
        this.browseResults = []
      } finally {
        this.browsingStorage = false
        this.loadingMore = false
      }
    },

    async loadMoreResults() {
      if (!this.nextContinuationToken || this.loadingMore) return
      await this.browseStorage(true)
    },

    selectStorageFile(file) {
      this.selectedStorageFile = file.path
      this.selectedStorageFileInfo = {
        ...file,
        key: file.path,
        filename: file.name
      }
    },

    getFilenameFromKey(key) {
      if (!key) return ''
      const parts = key.split('/')
      return parts[parts.length - 1]
    },

    formatFileSize(bytes) {
      if (!bytes) return 'N/A'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let size = bytes
      let unitIndex = 0
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }
      return `${size.toFixed(2)} ${units[unitIndex]}`
    },

    isVideoFile(filename) {
      if (!filename) return false
      const ext = filename.split('.').pop().toLowerCase()
      return ['ts', 'mxf', 'mp4', 'mov', 'avi', 'mkv', 'm4v', 'mpg', 'mpeg'].includes(ext)
    },

    // Video preview methods
    async previewFile(file) {
      this.previewFilename = file.name || file.filename
      this.previewFileData = file
      this.previewUrl = null
      this.previewError = null
      this.loadingPreview = true
      this.showVideoPreview = true
      this.usingProxyStream = false

      try {
        // HYBRID APPROACH: Try presigned URL first (direct from S3 - no backend load)
        // If CORS blocks it, fallback to proxy stream (through backend)
        const presignedResponse = await storageBrowseApi.getPresignedUrl(
          this.selectedStorageConfigId,
          file.path || file.key
        )

        // Test if presigned URL works (CORS check via HEAD request)
        try {
          await this.testCorsAccess(presignedResponse.url)
          // CORS works - use direct presigned URL (most efficient)
          this.previewUrl = presignedResponse.url
          console.log('Using direct presigned URL (CORS OK)')
        } catch (corsError) {
          // CORS blocked - fallback to proxy stream
          console.log('CORS blocked, falling back to proxy stream')
          this.previewUrl = storageBrowseApi.getProxyStreamUrl(
            this.selectedStorageConfigId,
            file.path || file.key
          )
          this.usingProxyStream = true
        }
      } catch (error) {
        console.error('Failed to get preview URL:', error)
        this.previewError = 'Failed to load video preview. The file format may not be supported for browser playback.'
      } finally {
        this.loadingPreview = false
      }
    },

    /**
     * Test if a URL is accessible (CORS check)
     * Returns promise that resolves if accessible, rejects if CORS blocked
     */
    async testCorsAccess(url) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('HEAD', url, true)
        xhr.timeout = 5000 // 5 second timeout
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 400) {
            resolve()
          } else {
            reject(new Error(`HTTP ${xhr.status}`))
          }
        }
        xhr.onerror = () => reject(new Error('CORS or network error'))
        xhr.ontimeout = () => reject(new Error('Timeout'))
        xhr.send()
      })
    },

    closeVideoPreview() {
      // Pause video if playing
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
      }
      this.showVideoPreview = false
      this.previewUrl = null
      this.previewFilename = ''
      this.previewFileData = null
      this.previewError = null
    },

    handleVideoError() {
      this.previewError = 'Failed to play video. The file format (.ts, .mxf) may not be supported for browser playback. Try downloading the file to preview locally.'
    },

    handleMediaPlayerError(errorData) {
      // Handle error from MediaPlayer component
      this.previewError = errorData.error || 'Failed to play video. The file format may not be supported for browser playback.'
    },

    async selectFileFromPreview() {
      if (!this.previewFileData) return

      // Select the file
      this.selectStorageFile(this.previewFileData)
      // Close preview
      this.closeVideoPreview()
    },

    async linkStorageFile() {
      if (!this.selectedStorageFile || !this.selectedStorageConfigId) return

      this.linkingFile = true
      try {
        // Call API to link the existing file as TX file
        const requestBody = {
          storageConfigId: this.selectedStorageConfigId,
          s3Key: this.selectedStorageFile,
          fileSize: this.selectedStorageFileInfo?.size || null
        }

        if (this.episodeId) {
          requestBody.episodeId = this.episodeId
        } else {
          requestBody.mediaId = this.mediaId
        }

        const response = await axios.post('/api/media-assets/link-existing', requestBody)

        this.$buefy.toast.open({
          message: response.data.message || 'File linked successfully',
          type: 'is-success',
          duration: 5000
        })

        // Reload asset list
        await this.loadData()

        // Emit event for parent to refresh
        this.$emit('upload-complete', {
          storagePath: this.selectedStorageFile,
          qcJobId: response.data.qcJobId
        })

        // Reset state
        this.cancelSelection()
      } catch (error) {
        console.error('Failed to link file:', error)
        console.log('Error response data:', error.response?.data)

        // Handle filename mismatch error with accept and rename option
        if (error.response?.data?.error === 'Filename mismatch') {
          console.log('Filename mismatch detected - showing Accept and Rename dialog')
          const data = error.response.data
          this.$buefy.dialog.confirm({
            title: 'Filename Mismatch',
            message: `
              <p><strong>${data.message}</strong></p>
              <p class="has-margin-top-10">
                <strong>Expected:</strong><br/>
                <code>${data.expectedFilename}</code>
              </p>
              <p class="has-margin-top-10">
                <strong>Selected:</strong><br/>
                <code>${data.actualFilename}</code>
              </p>
              <p class="has-margin-top-15">
                Click <strong>Accept and Rename</strong> to link this file and have the output renamed
                to the expected TX filename during processing.
              </p>
            `,
            type: 'is-warning',
            hasIcon: true,
            confirmText: 'Accept and Rename',
            cancelText: 'Cancel',
            onConfirm: () => {
              // Re-submit with skipFilenameValidation flag
              this.linkStorageFileWithOverride()
            }
          })
        } else {
          this.$buefy.toast.open({
            message: error.response?.data?.message || error.response?.data?.error || 'Failed to link file',
            type: 'is-danger',
            duration: 5000
          })
        }
      } finally {
        this.linkingFile = false
      }
    },

    /**
     * Link storage file with filename mismatch override
     * Output will be renamed to expected TX filename during processing
     */
    async linkStorageFileWithOverride() {
      if (!this.selectedStorageFile || !this.selectedStorageConfigId) return

      this.linkingFile = true
      try {
        const requestBody = {
          storageConfigId: this.selectedStorageConfigId,
          s3Key: this.selectedStorageFile,
          fileSize: this.selectedStorageFileInfo?.size || null,
          skipFilenameValidation: true // Override the filename mismatch check
        }

        if (this.episodeId) {
          requestBody.episodeId = this.episodeId
        } else {
          requestBody.mediaId = this.mediaId
        }

        const response = await axios.post('/api/media-assets/link-existing', requestBody)

        this.$buefy.toast.open({
          message: 'File linked successfully. Output will be renamed to expected TX filename.',
          type: 'is-success',
          duration: 5000
        })

        // Reload asset list
        await this.loadData()

        // Emit event for parent to refresh
        this.$emit('upload-complete', {
          storagePath: this.selectedStorageFile,
          qcJobId: response.data.qcJobId,
          filenameOverridden: true
        })

        // Reset state
        this.cancelSelection()
      } catch (error) {
        console.error('Failed to link file with override:', error)
        this.$buefy.toast.open({
          message: error.response?.data?.message || error.response?.data?.error || 'Failed to link file',
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.linkingFile = false
      }
    },

    remove(item) {
      this.$buefy.dialog.confirm({
        title: 'Delete Asset',
        message:
          'Are you sure you want to delete this asset? This does not delete the actual file from storage.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          await MediaAssetsApi.remove(item)
          await this.loadData()
          this.$buefy.toast.open({
            message: 'Asset record deleted',
            type: 'is-success'
          })
        }
      })
    },

    async startUpload(file) {
      if (!file) return

      this.uploading = true
      this.uploadProgress = 0
      this.uploadStatus = 'Getting upload URL...'
      this.uploadComplete = false
      this.qcJobId = null

      try {
        // Get file extension
        const fileExtension = file.name.split('.').pop()

        // Step 1: Get presigned URL with TX filename
        // Use episodeId for episodic content, mediaId for non-episodic
        const requestBody = {
          fileExtension: fileExtension
        }
        if (this.episodeId) {
          requestBody.episodeId = this.episodeId
        } else {
          requestBody.mediaId = this.mediaId
        }

        const presignedResponse = await axios.post(
          '/api/media-assets/presigned-url',
          requestBody
        )

        const { presignedUrl, s3Key, storagePath } = presignedResponse.data

        this.uploadStatus = 'Uploading to storage...'

        // Step 2: Upload directly to S3 using presigned URL
        await plainAxios.put(presignedUrl, file, {
          headers: {
            'Content-Type': 'application/octet-stream'
          },
          onUploadProgress: progressEvent => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            this.uploadStatus = `Uploading... ${this.uploadProgress}%`
          }
        })

        this.uploadStatus = 'Confirming upload and triggering QC...'

        // Step 3: Confirm upload and trigger QC
        const confirmBody = {
          s3Key: s3Key,
          storagePath: storagePath,
          fileSize: file.size
        }
        if (this.episodeId) {
          confirmBody.episodeId = this.episodeId
        } else {
          confirmBody.mediaId = this.mediaId
        }

        const confirmResponse = await axios.post(
          '/api/media-assets/confirm-upload',
          confirmBody
        )

        this.uploadComplete = true
        this.qcJobId = confirmResponse.data.qcJobId
        this.uploading = false
        this.showUpload = false
        this.fileToUpload = null

        // Reload asset list
        await this.loadData()

        // Emit event for parent to refresh
        this.$emit('upload-complete', {
          storagePath,
          qcJobId: this.qcJobId
        })

        this.$buefy.toast.open({
          message: confirmResponse.data.message,
          type: 'is-success',
          duration: 5000
        })
      } catch (error) {
        console.error('Upload error:', error)
        this.uploading = false
        this.uploadStatus = 'Upload failed'

        // Handle filename mismatch error with detailed message
        if (error.response?.data?.error === 'Filename mismatch') {
          const data = error.response.data
          this.$buefy.dialog.alert({
            title: 'Filename Mismatch',
            message: `
              <p><strong>${data.message}</strong></p>
              <p class="has-margin-top-10">
                <strong>Expected:</strong><br/>
                <code>${data.expectedFilename}</code>
              </p>
              <p class="has-margin-top-10">
                <strong>Uploaded:</strong><br/>
                <code>${data.actualFilename}</code>
              </p>
              <p class="has-margin-top-15 has-text-grey">
                Please rename the file to match the expected TX filename before uploading.
              </p>
            `,
            type: 'is-danger',
            hasIcon: true
          })
        } else {
          const errorMessage =
            error.response?.data?.message || error.response?.data?.error || error.message || 'Upload failed'

          this.$buefy.toast.open({
            message: `Error: ${errorMessage}`,
            type: 'is-danger',
            duration: 5000
          })
        }
      }
    }
  }
}
</script>

<style scoped>
.has-margin-top-15 {
  margin-top: 15px;
}

.has-margin-bottom-5 {
  margin-bottom: 5px;
}

.has-margin-left-10 {
  margin-left: 10px;
}

.file-list-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-top: 10px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.file-item:hover {
  background-color: #f5f5f5;
}

.file-item.is-selected {
  background-color: #e8f4fd;
  border-left: 3px solid #3273dc;
}

.file-item:last-child {
  border-bottom: none;
}

.file-name {
  flex: 1;
  margin-left: 8px;
  word-break: break-all;
}

.file-size {
  margin-left: 12px;
  white-space: nowrap;
}

.storage-browser {
  background: #fafafa;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

/* Folder grid styles */
.folder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.folder-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.folder-item:hover {
  background: #f0f7ff;
  border-color: #3273dc;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.folder-name {
  margin-left: 8px;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Breadcrumb override for better visibility */
.breadcrumb a {
  color: #3273dc;
}

.breadcrumb li.is-active a {
  color: #363636;
  font-weight: 600;
}

.file-actions {
  margin-left: auto;
  padding-left: 12px;
}

/* Video preview styles */
.video-preview-body {
  padding: 0 !important;
  background: #1a1a1a;
}

.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mt-4 {
  margin-top: 1rem;
}
</style>
